import * as React from 'react';
import styles from './index.less';
import { Button } from 'antd';
import FIcons from '@/components/FIcons';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import dayjs from 'dayjs';
import fMessage from '@/components/fMessage';
import * as AHooks from 'ahooks';
import FRectBtn from '@/components/FButton/FRectBtn';
import FComponentsLib from '@freelog/components-lib';
import FTooltip from '@/components/FTooltip';
import { connect } from 'dva';
import { ConnectState, NodeManagerModelState } from '@/models/connect';
import { Dispatch } from 'redux';
import FNoDataTip from '@/components/FNoDataTip';
import FWithdrawModal from './FWithdrawModal';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
// import { history } from 'umi';

interface IncomeProps {
  dispatch: Dispatch;
  nodeManagerPage: NodeManagerModelState;
}

interface WithdrawalRecord {
  id: string;
  date: string;
  amount: string;
  method: string;
  cardNumber: string;
  description: string;
  status: 'processing' | 'success' | 'failed';
  failureReason?: string;
}

interface IncomeStates {
  noWithdrawable: {
    reason: '' | 'noAccount' | 'noBankCard' | 'noWithdrawableAmount' | 'within24Hours';
    message: string;
  };
  accountId: string;
  withdrawableAmount: number;
  withdrawableAmountFree: number;
  last30DaysIncome: number;
  totalIncome: number;
  withdrawalRecords: WithdrawalRecord[];
  showWithdrawModal: boolean;
}

const initStates: IncomeStates = {
  noWithdrawable: {
    reason: '',
    message: '',
  },
  accountId: '',
  withdrawableAmount: -1,
  withdrawableAmountFree: -1,
  last30DaysIncome: -1,
  totalIncome: -1,
  withdrawalRecords: [],
  showWithdrawModal: false,
};

function Income({ dispatch, nodeManagerPage }: IncomeProps) {
  const [$noWithdrawable, set$noWithdrawable, get$noWithdrawable] = FUtil.Hook.useGetState<
    IncomeStates['noWithdrawable']
  >(initStates['noWithdrawable']);
  const [$accountId, set$accountId, get$accountId] = FUtil.Hook.useGetState<
    IncomeStates['accountId']
  >(initStates['accountId']);
  const [$withdrawableAmount, set$withdrawableAmount, get$withdrawableAmount] =
    FUtil.Hook.useGetState<IncomeStates['withdrawableAmount']>(initStates['withdrawableAmount']);
  const [$withdrawableAmountFree, set$withdrawableAmountFree, get$withdrawableAmountFree] = FUtil.Hook.useGetState<
    IncomeStates['withdrawableAmountFree']
  >(initStates['withdrawableAmountFree']);
  const [$last30DaysIncome, set$last30DaysIncome, get$last30DaysIncome] = FUtil.Hook.useGetState<
    IncomeStates['last30DaysIncome']
  >(initStates['last30DaysIncome']);
  const [$totalIncome, set$totalIncome, get$totalIncome] = FUtil.Hook.useGetState<
    IncomeStates['totalIncome']
  >(initStates['totalIncome']);
  const [$withdrawalRecords, set$withdrawalRecords, get$withdrawalRecords] = FUtil.Hook.useGetState<
    IncomeStates['withdrawalRecords']
  >(initStates['withdrawalRecords']);

  const [$showWithdrawModal, set$showWithdrawModal, get$showWithdrawModal] = FUtil.Hook.useGetState<
    IncomeStates['showWithdrawModal']
  >(initStates['showWithdrawModal']);

  AHooks.useMount(async () => {
    handleStatus();
  });

  async function handleStatus() {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        status: number;
        msg?: string;
        data?: {
          latestOperationDate?: string;
          availableBalance?: number;
        };
      };
    } = await FServiceAPI.Payment.queryWithdrawStatus({
      ownerId: nodeManagerPage.nodeId,
      accountType: 2,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    if (data.status === 1) {
      set$noWithdrawable({
        reason: 'noAccount',
        message:
          // 还未设置结算信息，这是必要的提现条件
          FI18n.i18nNext.t('payouts_info_addpayoutmethod'),
      });
    } else if (data.status === 2) {
      set$noWithdrawable({
        reason: 'noWithdrawableAmount',
        message: '',
      });
    } else if (data.status === 3) {
      set$noWithdrawable({
        reason: 'within24Hours',
        message:
          FI18n.i18nNext.t('payouts_msg_24hourwaiting', {
            timer: dayjs(data.data?.latestOperationDate)
              .add(1, 'day')
              .format('YYYY-MM-DD HH:mm:ss')
          })
      });
    } else if (data.status === 4) {
      set$noWithdrawable({
        reason: 'noBankCard',
        message:
          //  '还未设置提现银行卡，这是必要的提现条件',
          FI18n.i18nNext.t('payouts_info_addpayoutmethod'),
      });
    }

    if (data.status !== 1) {
      handleListData();
    }
  }

  async function handleListData() {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        accountType: 1;
        availableBalance: number;
        certName: string;
        certNo: string;
        createDate: string;
        currencyType: number;
        freezeBalance: number;
        id: string;
        ownerId: number;
        ownerName: string;
        ownerUserId: number;
        ownerUserName: string;
        status: number;
        totalBalance: number;
        totalExpenditureAmount: number;
        totalIncomeAmount: number;
      };
    } = await FServiceAPI.Payment.queryFinancialAccountInfo({
      accountType: 2,
      ownerId: nodeManagerPage.nodeId,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    if (!data) {
      return;
    }
    set$accountId(data.id);
    set$withdrawableAmount(data.availableBalance);

    // 与 dashboard 节点「近30日收益」同一接口；概览为全部节点统计，此处限定当前节点 ownerId
    const startTime = dayjs().subtract(29, 'days').format(FUtil.Predefined.momentDateFormat);
    const endTime = dayjs().format(FUtil.Predefined.momentDateFormat);
    const {
      ret: retLast30,
      errCode: errCodeLast30,
      msg: msgLast30,
      data: dataLast30,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: { depositAmount: number; withdrawalAmount: number };
    } = await FServiceAPI.Payment.queryStatistics({
      ownerType: 2,
      ownerId: nodeManagerPage.nodeId,
      startTransactionDate: startTime,
      endTransactionDate: endTime,
    });
    if (retLast30 !== 0 || errCodeLast30 !== 0) {
      fMessage(msgLast30, 'error');
      return;
    }
    set$last30DaysIncome(dataLast30.depositAmount);
    set$totalIncome(data.totalIncomeAmount);

    const {
      ret: ret2,
      errCode: errCode2,
      msg: msg2,
      data: data2,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        dataList: {
          id: string;
          transactionInitiationDate: string;
          transactionAmount: number;
          cardNo: string;
          status: 1 | 2 | 3; // 提现状态 1:处理中 2:成功 3:失败
          remark: string;
          withdrawCashType: string;
        }[];
      };
    } = await FServiceAPI.Payment.queryWithdrawCashList({
      accountId: data.id,
      skip: 0,
      limit: 100,
    });
    if (ret2 !== 0 || errCode2 !== 0) {
      fMessage(msg, 'error');
      return;
    }
    set$withdrawalRecords(
      data2.dataList.map((item) => ({
        id: item.id,
        date: dayjs(item.transactionInitiationDate).format('YYYY-MM-DD HH:mm:ss'),
        amount: item.transactionAmount.toString(),
        method: item.withdrawCashType,
        cardNumber: item.cardNo,
        description: '手动提现',
        status: item.status === 1 ? 'processing' : item.status === 2 ? 'success' : 'failed',
        failureReason: item.status === 3 ? item.remark : undefined,
      })),
    );

    if (data.availableBalance > 0) {
      const { ret: ret3, errCode: errCode3, msg: msg3, data: data_free }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          feeAmount: number;
        };
      } = await FServiceAPI.Payment.queryFee({
        accountId: data.id,
        transactionAmount: data.availableBalance,
      });

      set$withdrawableAmountFree(data_free.feeAmount);
    } else {
      set$withdrawableAmountFree(0);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {
          // 收入结算
          FI18n.i18nNext.tAuto('nodemgnt_payouts_title')
        }
      </div>

      <div className={styles.incomeModule}>
        <div className={styles.withdrawableCard}>
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <span className={styles.cardTitle}>
                {
                  // 可提现金额（元）
                  FI18n.i18nNext.tAuto('payouts_availabletowithdraw')
                }
              </span>
              <div className={styles.infoIcon}>
                <FTooltip
                  styles={{ body: { width: 340 } }}
                  placement={'bottomRight'}
                  title={
                    <div className={styles.tooltipTitle}>
                      {/* <div>可提现金额是完成结算可以提现的收入金额</div> */}
                      {
                        FI18n.i18nNext.tAuto('payouts_availabletowithdraw_info')
                      }
                      <div style={{ height: 5 }} />
                      {/* <div>
                        付款方完成支付后，收入会在指定时间进入待结算金额。待结算金额是扣除了交易费用的收入金额。
                      </div>
                      <div style={{ height: 20 }} />
                      <div>待结算金额（元）/ 费率0.6%</div> */}
                      {
                        FI18n.i18nNext.tAuto('payouts_availabletowithdraw_info2')
                      }
                    </div>
                  }
                >
                  <span>
                    <FIcons.FInfo
                      style={{ fontSize: 16, width: 16, height: 16, cursor: 'pointer' }}
                    />
                  </span>
                </FTooltip>
              </div>
            </div>
            {$accountId && (
              <a
                className={styles.settlementLink}
                onClick={() => {
                  self.open(
                    FUtil.LinkTo.settlementInfo({
                      accountID: get$accountId(),
                    }),
                  );
                }}
              >
                {
                  // 结算信息
                  FI18n.i18nNext.tAuto('payouts_btn_payoutsettings')
                }
              </a>
            )}
          </div>
          <div className={styles.cardContent}>
            <span className={styles.amountLarge}>
              {$withdrawableAmount === -1 ? '--' : $withdrawableAmount.toFixed(2)}
            </span>
            <div className={styles.withdrawActionArea}>
              {$noWithdrawable.reason !== '' && (
                <div className={styles.withdrawTip}>
                  <FComponentsLib.FContentText type={'additional1'} text={$noWithdrawable.message} />
                  {$noWithdrawable.reason === 'noAccount' && (
                    <FComponentsLib.FTextBtn
                      type={'primary'}
                      onClick={async () => {
                        const { data }: { data: { mobile: string } } = await FServiceAPI.User.currentUserInfo();
                        if (!data?.mobile) {
                          const bool = await fPromiseModalConfirm({
                            title: '提示',
                            description:
                              FI18n.i18nNext.t('payout_setting_alert_no_mobileno_msg'),
                            okText:
                              FI18n.i18nNext.t('payout_setting_alert_no_mobileno_btn_ok'),
                            cancelText:
                              FI18n.i18nNext.t('payout_setting_alert_no_mobileno_btn_later'),
                          });
                          if (!bool) return;
                          self.open(
                            FUtil.Format.completeUrlByDomain('user') +
                              FUtil.LinkTo.setting() +
                              '?type=security',
                          );
                          return;
                        }
                        self.open(
                          FUtil.LinkTo.settlementInfoCreator({
                            nodeID: nodeManagerPage.nodeId,
                          }),
                        );
                      }}
                      style={{ fontSize: 12 }}
                    >
                      {
                        // 前往设置
                        FI18n.i18nNext.tAuto('payouts_btn_addpayoutmethod')
                      }
                    </FComponentsLib.FTextBtn>
                  )}
                  {$noWithdrawable.reason === 'noBankCard' && (
                    <FComponentsLib.FTextBtn
                      type={'primary'}
                      onClick={() => {
                        self.open(
                          FUtil.LinkTo.settlementBankCardEditor({
                            accountID: get$accountId(),
                          }),
                        );
                      }}
                      style={{ fontSize: 12 }}
                    >
                      {
                        // 前往设置
                        FI18n.i18nNext.tAuto('payouts_btn_addpayoutmethod')
                      }
                    </FComponentsLib.FTextBtn>
                  )}
                </div>
              )}
              <FRectBtn
                disabled={$noWithdrawable.reason !== ''}
                type='primary'
                onClick={async () => {
                  set$showWithdrawModal(true);
                }}
              >
                {
                  // 提现
                  FI18n.i18nNext.tAuto('payouts_btn_withdraw')
                }
              </FRectBtn>
            </div>
          </div>
        </div>

        <div className={styles.statsCard}>
          <div className={styles.statItem}>
            <div className={styles.statHeader}>
              <span className={styles.statTitle}>
                {
                  // 近30天收入（元）
                  FI18n.i18nNext.tAuto('payouts_last30days')
                }
              </span>
            </div>
            <div className={styles.statValue}>
              <span className={styles.amountMedium}>
                {$last30DaysIncome === -1 ? '--' : $last30DaysIncome.toFixed(2)}
              </span>
            </div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.statItem}>
            <div className={styles.statHeader}>
              <span className={styles.statTitle}>
                {
                  // 总收入（元）
                  FI18n.i18nNext.tAuto('payouts_alltime')
                }
              </span>
            </div>
            <div className={styles.statValue}>
              <span className={styles.amountMedium}>
                {$totalIncome === -1 ? '--' : $totalIncome.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {
        $withdrawalRecords.length > 0 && (<div className={styles.withdrawalRecords}>
          <div className={styles.recordsTitle}>
            {
              // 提现记录
              FI18n.i18nNext.tAuto('nodemgnt_payouts_history_title')
            }
          </div>


          {$withdrawalRecords.length > 0 && (
            <div className={styles.recordsTable}>
              <div className={styles.tableHeader}>
                <div className={styles.headerCell}>
                  {
                    // 日期
                    FI18n.i18nNext.tAuto('payoutshistory_list_date')
                  }
                </div>
                <div className={styles.headerCell}>
                  {
                    // 金额（元）
                    FI18n.i18nNext.tAuto('payoutshistory_list_amount')
                  }
                </div>
                <div className={styles.headerCell}>
                  {
                    // 提现方式
                    FI18n.i18nNext.tAuto('payoutshistory_list_payoutmethod')
                  }
                </div>
                <div className={styles.headerCell}>
                  {
                    // 说明
                    FI18n.i18nNext.tAuto('payoutshistory_list_desc')
                  }
                </div>
                <div className={styles.headerCellStatus}>
                  {
                    // 状态
                    FI18n.i18nNext.tAuto('payoutshistory_list_status')
                  }
                </div>
              </div>
              <div className={styles.tableBody}>
                {$withdrawalRecords.map((record, index) => (
                  <React.Fragment key={record.id}>
                    <div className={styles.rowDivider}></div>
                    <div className={styles.tableRow}>
                      <div className={styles.tableCell}>{record.date} </div>
                      <div className={styles.tableCellAmount}>{record.amount}</div>
                      <div className={styles.tableCellMethod}>
                        <div>{record.method}</div>
                        <div>{record.cardNumber}</div>
                      </div>
                      <div className={styles.tableCell}>{record.description}</div>
                      <div className={styles.tableCellStatus}>
                        <span className={`${styles.statusText} ${getStatusColor(record.status)}`}>
                          {getStatusText(record.status)}
                        </span>
                        {record.status === 'failed' && (
                          <span className={styles.failedLink}>为何提现会失败？</span>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
                <div className={styles.rowDivider}></div>
              </div>
            </div>
          )}
        </div>)
      }


      <FWithdrawModal
        accountID={$accountId}
        open={$showWithdrawModal}
        onCancel={() => {
          set$showWithdrawModal(false);
        }}
        onOk={(amount) => {
          set$showWithdrawModal(false);
          // handleData();
          handleStatus();
        }}
        availableAmount={$withdrawableAmount}
      // accountInfo={'中国银行（4567）'}
      />
    </div>
  );
}

export default connect(({ nodeManagerPage }: ConnectState) => ({
  nodeManagerPage,
}))(Income);

function getStatusText(status: string) {
  switch (status) {
    case 'processing':
      return '处理中';
    case 'success':
      return '成功';
    case 'failed':
      return '失败';
    default:
      return '';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'processing':
      return styles.statusProcessing;
    case 'success':
      return styles.statusSuccess;
    case 'failed':
      return styles.statusFailed;
    default:
      return '';
  }
}
